import type { ReactNode } from "react";
import { cn } from "../lib/cn.js";
import { Breadcrumbs, useBreadcrumbTrail } from "../components/navigation/breadcrumbs.js";
import type { Breadcrumb } from "../components/navigation/breadcrumbs.js";

export interface PageHeaderProps {
  /** Explicit breadcrumbs; omitted = the layout's route trail (if any) followed by `title`. */
  breadcrumbs?: Breadcrumb[];
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
}

/**
 * Standard page header — breadcrumbs, title, short description, one dominant primary action —
 * per `17_UI_VISUAL_DESIGN_SYSTEM.md` §10.
 */
export function PageHeader({ breadcrumbs, title, description, actions, className }: PageHeaderProps) {
  const trail = useBreadcrumbTrail();
  const crumbs = breadcrumbs ?? (trail.length > 0 ? [...trail, { label: title }] : []);
  return (
    <div className={cn("mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", className)}>
      <div className="flex min-w-0 flex-col gap-1.5">
        <Breadcrumbs items={crumbs} />
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {description && <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
